import {
  StripeAddressElementOptions,
  StripeElementLocale,
  StripeElementsOptionsMode,
  StripePaymentElementOptions,
} from '@stripe/stripe-js'
import { createSelector } from 'reselect'

import { ManychatLegalEntity } from 'common/billing/constants/AccountBillingInfo'
import {
  defaultPaymentElementOptions,
  stripeElementsAppearance,
} from 'common/billing/constants/Stripe'
import {
  getBillingCurrency,
  getManychatLegalEntity,
} from 'common/billing/selectors/billingSelectors'
import { getAccountFlags, getUserLanguage } from 'common/core/selectors/appSelectors'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

export const getStripeOptions = createSelector(
  [getBillingCurrency, getUserLanguage],
  (currency, language): StripeElementsOptionsMode => {
    return {
      mode: 'setup',
      currency: currency.toLowerCase(),
      setupFutureUsage: 'on_session',
      paymentMethodCreation: 'manual',
      paymentMethodTypes: ['card'],
      locale: (language ?? 'en') as StripeElementLocale,
      appearance: stripeElementsAppearance,
      loader: 'always',
    }
  },
)

export const getPaymentElementOptions = createSelector(
  [getManychatLegalEntity],
  (legalEntity): StripePaymentElementOptions => {
    if (legalEntity === ManychatLegalEntity.Brazil) {
      return {
        ...defaultPaymentElementOptions,
        defaultValues: {
          billingDetails: {
            address: {
              country: 'BR',
            },
          },
        },
        fields: {
          billingDetails: {
            address: {
              country: 'auto',
              postalCode: 'auto',
              state: 'auto',
              city: 'auto',
              line1: 'auto',
              line2: 'auto',
            },
          },
        },
      }
    }

    return defaultPaymentElementOptions
  },
)

export const getShouldShowTaxIdInputOnCardForm = createSelector(
  getManychatLegalEntity,
  (legalEntity) => legalEntity === ManychatLegalEntity.Brazil,
)

export const getAddressElementOptions = createSelector(
  getManychatLegalEntity,
  (legalEntity): StripeAddressElementOptions | null => {
    if (legalEntity !== ManychatLegalEntity.Brazil) {
      return null
    }

    return {
      mode: 'billing',
      allowedCountries: ['BR'],
      defaultValues: {
        address: { country: 'BR' },
      },
    }
  },
)

export const getIsUpdatePaymentMethodInAppFlowEnabled = createSelector(
  getManychatLegalEntity,
  getAccountFlags,
  (legalEntity, flags): boolean => {
    return (
      legalEntity === ManychatLegalEntity.Brazil &&
      Boolean(flags?.[AccountFlag.ENABLE_IN_APP_CHECKOUT_BRAZIL])
    )
  },
)
